import { type RefObject, useCallback, useEffect, useRef } from 'react';

interface DelayedIntersectionObserverProps {
    onIntersecting?: () => void;
    delay?: number;
    rootMargin?: string;
    threshold?: number;
}

export const useDelayedIntersectionObserver = ({
    onIntersecting,
    delay,
    rootMargin = '',
    threshold = 0.1,
}: DelayedIntersectionObserverProps): RefObject<HTMLDivElement> => {
    const ref = useRef<HTMLDivElement>(null);

    const timers = useRef<Map<Element, number>>(new Map());

    const onIntersectingDelayedCallback = useCallback(
        (entries: IntersectionObserverEntry[]): void => {
            entries.forEach((entry: IntersectionObserverEntry) => {
                const el = entry.target;

                if (entry.isIntersecting) {
                    if (!timers.current.has(el)) {
                        const timerId = setTimeout(() => {
                            onIntersecting?.();
                            timers.current.delete(el);
                        }, delay) as unknown as number;

                        timers.current.set(el, timerId);
                    }
                } else if (timers.current.has(el)) {
                    clearTimeout(timers.current.get(el));
                    timers.current.delete(el);
                }
            });
        },
        [onIntersecting]
    );

    const observer = useRef<IntersectionObserver | null>(null);

    const unobserve = useCallback(() => {
        if (!ref.current) {
            return;
        }

        observer.current?.unobserve(ref.current);
        observer.current?.disconnect();
    }, []);

    useEffect(() => {
        if (!ref.current) {
            return;
        }

        if (observer.current) {
            unobserve();
        }

        // eslint-disable-next-line compat/compat
        observer.current = new IntersectionObserver(onIntersectingDelayedCallback, { threshold, rootMargin });
        observer.current?.observe(ref.current);

        return () => unobserve();
    }, [onIntersectingDelayedCallback, unobserve, rootMargin]);

    return ref;
};
